import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetBranchEmployeesOrderQuery } from '@app/api/Employees';
import { useGetBranchEmployeesQuery } from '@app/api/Users/api';
import type { GetBranchEmployeesResponse } from '@app/api/Users/types';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useErrorHandler } from '@app/shared/hooks';

interface UseExecutorSelectModelProps {
  mode: ExecutorSelectMode;
  currentEmployeeId?: number;
  excludedEmployeeIds?: string[];
  canCreateWorkScheduleOverride?: boolean;
  shouldSelectExecutorOnAddWorkingDay?: boolean;
  onOpenIndividualCalendar?: (employeeId: number) => void;
  onSelectEmployee?: (employeeId: number) => void;
}

export type ExecutorSelectMode =
  | 'appointment-journal'
  | 'individual-appointment-journal';

export interface ExecutorOption {
  employeeId: number;
  label: string;
  firstName: string;
  lastName: string;
}

export interface SelectedExecutor {
  employeeId: number;
  label: string;
}

export interface ExecutorSelectWorkScheduleModal {
  isOpen: boolean;
  employeeId: number;
  employeeName: string;
}

export interface ExecutorSelectModel {
  currentEmployee: ExecutorOption | null;
  visibleEmployees: ExecutorOption[];
  isDisabled: boolean;
  isLoading: boolean;
  isDropdownOpen: boolean;
  mode: ExecutorSelectMode;
  isEmployeeSelectable: boolean;
  canOpenIndividualCalendar: boolean;
  searchText: string;
  selectedExecutor: SelectedExecutor | null;
  workScheduleModal: ExecutorSelectWorkScheduleModal;
  canCreateWorkSchedule: boolean;
  handleSearch: (value: string) => void;
  handleDropdownOpenChange: (value: boolean) => void;
  handleOpenIndividualCalendar: (employeeId: number) => void;
  handleOpenAddWorkSchedule: (employeeId: number, employeeName: string) => void;
  handleCloseWorkScheduleModal: () => void;
  handleSelectEmployee: (employeeId: number, label: string) => void;
}

type BranchEmployee = GetBranchEmployeesResponse[number];

const getExecutorLabel = ({
  preparedLabel,
  firstName,
  lastName,
}: {
  preparedLabel?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}) =>
  preparedLabel?.trim() ||
  `${lastName || ''} ${firstName || ''}`.trim() ||
  'Исполнитель без имени';

export const useExecutorSelectModel = ({
  mode,
  currentEmployeeId,
  excludedEmployeeIds,
  canCreateWorkScheduleOverride,
  shouldSelectExecutorOnAddWorkingDay = false,
  onOpenIndividualCalendar,
  onSelectEmployee,
}: UseExecutorSelectModelProps): ExecutorSelectModel => {
  const {
    params: { ActiveBranchId, UserId },
  } = useUserPrint();
  const {
    userAccessRights: {
      Records: { IsViewAllRecords },
      WorkSchedules: { IsCreate },
    },
  } = useNewUserAccessRights();

  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedExecutor, setSelectedExecutor] =
    useState<SelectedExecutor | null>(null);
  const [workScheduleModal, setWorkScheduleModal] = useState({
    isOpen: false,
    employeeId: 0,
    employeeName: '',
  });

  const {
    data: branchEmployeesData,
    isLoading: isBranchEmployeesOrderLoading,
    isFetching: isBranchEmployeesOrderFetching,
    error: branchEmployeesOrderError,
    isError: isBranchEmployeesOrderError,
  } = useGetBranchEmployeesOrderQuery(
    { ActiveBranchId },
    { skip: ActiveBranchId == null }
  );

  const {
    data: branchEmployees = [],
    isLoading: isBranchEmployeesLoading,
    isFetching: isBranchEmployeesFetching,
    error: branchEmployeesError,
    isError: isBranchEmployeesError,
  } = useGetBranchEmployeesQuery(
    { branchId: ActiveBranchId, ActiveBranchId },
    { skip: ActiveBranchId == null }
  );

  useErrorHandler([
    { flag: isBranchEmployeesOrderError, error: branchEmployeesOrderError },
    { flag: isBranchEmployeesError, error: branchEmployeesError },
  ]);

  const branchEmployeesWithServices = useMemo(
    () =>
      branchEmployees.filter(
        ({ CountClientServices }) => CountClientServices > 0
      ),
    [branchEmployees]
  );

  const branchEmployeesById = useMemo(
    () =>
      new Map<number, BranchEmployee>(
        branchEmployeesWithServices.map((employee) => [employee.Id, employee])
      ),
    [branchEmployeesWithServices]
  );

  const allEmployees = useMemo<ExecutorOption[]>(() => {
    const orderedEmployees = branchEmployeesData?.Employees ?? [];

    const preparedOrderedEmployees = orderedEmployees.flatMap((employee) => {
      const branchEmployee = branchEmployeesById.get(employee.EmployeeId);

      if (!branchEmployee) {
        return [];
      }

      const { Id, NameFirst, NameLast } = branchEmployee;

      return {
        employeeId: Id,
        label: getExecutorLabel({
          preparedLabel: employee.Employee,
          firstName: NameFirst,
          lastName: NameLast,
        }),
        firstName: NameFirst || '',
        lastName: NameLast || '',
      };
    });

    const includedEmployeeIds = new Set(
      preparedOrderedEmployees.map(({ employeeId }) => employeeId)
    );

    const missingEmployees = branchEmployeesWithServices
      .filter(({ Id }) => !includedEmployeeIds.has(Id))
      .map(({ Id, NameFirst, NameLast }) => ({
        employeeId: Id,
        label: getExecutorLabel({
          firstName: NameFirst,
          lastName: NameLast,
        }),
        firstName: NameFirst || '',
        lastName: NameLast || '',
      }));

    return [...preparedOrderedEmployees, ...missingEmployees];
  }, [branchEmployeesById, branchEmployeesData, branchEmployeesWithServices]);

  const accessFilteredEmployees = useMemo<ExecutorOption[]>(() => {
    const excludedEmployeeIdsSet = new Set(excludedEmployeeIds);
    const availableEmployees = excludedEmployeeIdsSet.size
      ? allEmployees.filter(
          ({ employeeId }) => !excludedEmployeeIdsSet.has(String(employeeId))
        )
      : allEmployees;

    if (IsViewAllRecords) {
      return availableEmployees;
    }

    return availableEmployees.filter(({ employeeId }) => employeeId === UserId);
  }, [IsViewAllRecords, UserId, allEmployees, excludedEmployeeIds]);

  const currentEmployee = useMemo(() => {
    if (currentEmployeeId == null) {
      return null;
    }

    return (
      allEmployees.find(({ employeeId }) => employeeId === currentEmployeeId) ||
      null
    );
  }, [allEmployees, currentEmployeeId]);

  const visibleEmployees = useMemo<ExecutorOption[]>(() => {
    if (!searchText) {
      return accessFilteredEmployees;
    }

    const lowerCaseSearch = searchText.toLowerCase();

    return accessFilteredEmployees.filter(
      ({ label, firstName, lastName }) =>
        label.toLowerCase().includes(lowerCaseSearch) ||
        firstName.toLowerCase().includes(lowerCaseSearch) ||
        lastName.toLowerCase().includes(lowerCaseSearch)
    );
  }, [accessFilteredEmployees, searchText]);

  const effectiveSelectedExecutor = useMemo<SelectedExecutor | null>(() => {
    if (currentEmployee) {
      return {
        employeeId: currentEmployee.employeeId,
        label: currentEmployee.label,
      };
    }

    if (currentEmployeeId != null) {
      return selectedExecutor?.employeeId === currentEmployeeId
        ? selectedExecutor
        : null;
    }

    return selectedExecutor;
  }, [currentEmployee, currentEmployeeId, selectedExecutor]);

  const isLoading = useMemo(
    () =>
      isBranchEmployeesOrderLoading ||
      isBranchEmployeesLoading ||
      isBranchEmployeesOrderFetching ||
      isBranchEmployeesFetching,
    [
      isBranchEmployeesOrderFetching,
      isBranchEmployeesOrderLoading,
      isBranchEmployeesFetching,
      isBranchEmployeesLoading,
    ]
  );

  const isDisabled = useMemo(
    () => accessFilteredEmployees.length === 0 || isLoading,
    [accessFilteredEmployees.length, isLoading]
  );

  const isEmployeeSelectable = mode === 'individual-appointment-journal';
  const canOpenIndividualCalendar =
    mode === 'appointment-journal' && Boolean(onOpenIndividualCalendar);

  useEffect(() => {
    if (currentEmployeeId == null) {
      return;
    }

    if (!currentEmployee) {
      setSelectedExecutor((prev) =>
        prev?.employeeId === currentEmployeeId ? prev : null
      );

      return;
    }

    const nextLabel = currentEmployee.label;

    setSelectedExecutor((prev) => {
      if (prev?.employeeId === currentEmployeeId && prev.label === nextLabel) {
        return prev;
      }

      return {
        employeeId: currentEmployeeId,
        label: nextLabel,
      };
    });
  }, [currentEmployee, currentEmployeeId]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const handleDropdownOpenChange = useCallback((value: boolean) => {
    setIsDropdownOpen(value);

    if (!value) {
      setSearchText('');
    }
  }, []);

  const handleOpenIndividualCalendar = useCallback(
    (employeeId: number) => {
      onOpenIndividualCalendar?.(employeeId);
      handleDropdownOpenChange(false);
    },
    [handleDropdownOpenChange, onOpenIndividualCalendar]
  );

  const handleOpenAddWorkSchedule = useCallback(
    (employeeId: number, employeeName: string) => {
      if (shouldSelectExecutorOnAddWorkingDay) {
        setSelectedExecutor({ employeeId, label: employeeName });
      }

      setWorkScheduleModal({ isOpen: true, employeeId, employeeName });
      handleDropdownOpenChange(false);
    },
    [handleDropdownOpenChange, shouldSelectExecutorOnAddWorkingDay]
  );

  const handleCloseWorkScheduleModal = useCallback(() => {
    setWorkScheduleModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleSelectEmployee = useCallback(
    (employeeId: number, label: string) => {
      if (!isEmployeeSelectable) {
        return;
      }

      setSelectedExecutor({ employeeId, label });
      handleDropdownOpenChange(false);
      onSelectEmployee?.(employeeId);
    },
    [handleDropdownOpenChange, isEmployeeSelectable, onSelectEmployee]
  );

  return {
    currentEmployee,
    visibleEmployees,
    isDisabled,
    isLoading,
    isDropdownOpen,
    mode,
    isEmployeeSelectable,
    canOpenIndividualCalendar,
    searchText,
    selectedExecutor: effectiveSelectedExecutor,
    workScheduleModal,
    canCreateWorkSchedule: canCreateWorkScheduleOverride ?? IsCreate,
    handleSearch,
    handleDropdownOpenChange,
    handleOpenIndividualCalendar,
    handleOpenAddWorkSchedule,
    handleCloseWorkScheduleModal,
    handleSelectEmployee,
  };
};
